import { DateTimeFormatter, LocalDate, Period } from '@js-joda/core';
import { timeToMinutes } from '../../utils/timeToMinutes';
import { minutesToTime } from '../../utils/minutesToTime';
const dateformatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

export function calculateDragChanges(state, date, itemId, dragOverTime) {
    const dragStartInfo = state.dragStartInfo;
    const draggedSlotStart = dragStartInfo.draggedSlot;
    const affectedSlots = dragStartInfo.affectedSlots;
    const affectedSlotsIds = Object.keys(affectedSlots).map(Number);

    const mode = state.mode;

    let tasks = {};
    let slotTask = null;
    if (mode === 'project') {
        slotTask = itemId;
        const previousSlotTask = state.slots[draggedSlotStart.slotId].task_id;
        let itemsToReload = [previousSlotTask, slotTask];
        // For each item that needs to be reloaded, we change the linked slots
        itemsToReload.forEach((taskId) => {
            let task = _.cloneDeep(state.tasks[taskId]);
            // Add/remove the slot from the task if its linked/not linked.
            if (slotTask !== taskId) {
                task.slots = _.difference(task.slots, affectedSlotsIds);
            } else {
                task.slots = _.union(task.slots, affectedSlotsIds);
            }
            tasks[taskId] = task;
        });
    }

    let persons = {};
    let slotPersons = {};
    if (mode === 'person') {
        slotPersons = _.cloneDeep(draggedSlotStart.persons);

        if (draggedSlotStart.personId !== itemId) {
            // Remove the id of the person where the dragged started
            delete slotPersons[draggedSlotStart.personId];

            // Add the person of where is dragging
            if (!slotPersons[itemId]) {
                slotPersons[itemId] = {
                    personId: itemId,
                    teams: [],
                };
            }
        }

        // Get the persons that are linked to the slot right now (while dragging)
        const previousSlotPersons = _.cloneDeep(state.slots[draggedSlotStart.slotId].persons);
        // Linked persons from the start and new ones need to be reloaded
        let itemsToReload = [
            ...new Set([...Object.keys(previousSlotPersons), ...Object.keys(slotPersons)]),
        ];

        // For each item that needs to be reloaded, we change the linked slots
        itemsToReload.forEach((personId) => {
            let person = _.cloneDeep(state.persons[personId]);
            // Add/remove the slot from the person if its linked/not linked.
            if (!slotPersons[personId]) {
                person.slots = _.difference(person.slots, affectedSlotsIds);
            } else {
                person.slots = _.union(person.slots, affectedSlotsIds);
            }
            persons[personId] = person;
        });
    }

    let equipments = {};
    let slotEquipments = {};
    if (mode === 'equipment') {
        slotEquipments = _.cloneDeep(draggedSlotStart.equipments);

        if (draggedSlotStart.equipmentId !== itemId) {
            // Remove the id of the equipment where the dragged started
            delete slotEquipments[draggedSlotStart.equipmentId];

            // Add the equipment of where is dragging
            if (!slotEquipments[itemId]) {
                slotEquipments[itemId] = {
                    equipmentId: itemId,
                    teams: [],
                };
            }
        }

        // Get the equipments that are linked to the slot right now (while dragging)
        const previousSlotEquipments = _.cloneDeep(state.slots[draggedSlotStart.slotId].equipments);
        // Linked equipments from the start and new ones need to be reloaded
        let itemsToReload = [
            ...new Set([...Object.keys(previousSlotEquipments), ...Object.keys(slotEquipments)]),
        ];

        // For each item that needs to be reloaded, we change the linked slots
        itemsToReload.forEach((equipmentId) => {
            let equipment = _.cloneDeep(state.equipments[equipmentId]);
            // Add/remove the slot from the equipment if its linked/not linked.
            if (!slotEquipments[equipmentId]) {
                equipment.slots = _.difference(equipment.slots, affectedSlotsIds);
            } else {
                equipment.slots = _.union(equipment.slots, affectedSlotsIds);
            }
            equipments[equipmentId] = equipment;
        });
    }

    let teams = {};
    let slotTeams = {};
    if (mode === 'team') {
        slotPersons = _.cloneDeep(draggedSlotStart.persons);
        slotEquipments = _.cloneDeep(draggedSlotStart.equipments);
        slotTeams = _.cloneDeep(draggedSlotStart.teams);

        if (draggedSlotStart.teamId !== itemId) {
            // Remove the id of the team where the dragged started
            let slotTeam = slotTeams[draggedSlotStart.teamId];
            delete slotTeams[draggedSlotStart.teamId];

            // Remove the persons linked with the slot
            slotTeam.persons.forEach((personId) => {
                delete slotPersons[personId];
            });
            slotTeam.equipments.forEach((equipmentIds) => {
                delete slotEquipments[equipmentIds];
            });

            // Add the team of where is dragging
            if (!slotTeams[itemId]) {
                slotTeams[itemId] = {
                    teamId: itemId,
                    persons: [],
                    equipments: [],
                };
            }

            // Add the persons of the team thas is dragging
            const teamPersons = state.teams[itemId].persons;
            teamPersons.forEach((personId) => {
                slotPersons[personId] = {
                    personId: personId,
                    teams: [itemId],
                };
            });
            const teamEquipments = state.teams[itemId].equipments;
            teamEquipments.forEach((equipmentId) => {
                slotEquipments[equipmentId] = {
                    equipmentId: equipmentId,
                    teams: [itemId],
                };
            });
        }

        // Get the teams that are linked to the slot right now (while dragging)
        const previousSlotTeams = _.cloneDeep(state.slots[draggedSlotStart.slotId].teams);
        // Linked teams from the start and new ones need to be reloaded
        let itemsToReload = [
            ...new Set([...Object.keys(previousSlotTeams), ...Object.keys(slotTeams)]),
        ];

        // For each item that needs to be reloaded, we change the linked slots
        itemsToReload.forEach((teamId) => {
            let team = _.cloneDeep(state.teams[teamId]);
            // Add/remove the slot from the team if its linked/not linked.
            if (!slotTeams[teamId]) {
                team.slots = _.difference(team.slots, affectedSlotsIds);
            } else {
                team.slots = _.union(team.slots, affectedSlotsIds);
            }
            teams[teamId] = team;
        });
    }

    // Change dates and times
    date = LocalDate.parse(date);
    const startDate = draggedSlotStart.localDate;
    const daysDifference = Period.between(startDate, date).days();

    // New Times
    const dragTime = draggedSlotStart.dragTime;
    const minMinutes = 0;
    const maxMinutes = 60 * 24 - 5;
    let timeMinutesDifference = 0;
    if (dragTime) {
        timeMinutesDifference = timeToMinutes(dragOverTime) - timeToMinutes(dragTime);
    }

    let slots = {};
    affectedSlotsIds.forEach((slotId) => {
        let slot = _.cloneDeep(affectedSlots[slotId]);
        // Time
        if (timeMinutesDifference !== 0) {
            const timeStart = slot.time_start;
            let timeStartMinutesNew = timeToMinutes(timeStart) + timeMinutesDifference;

            const timeEnd = slot.time_end;
            let timeEndMinutesNew = timeToMinutes(timeEnd) + timeMinutesDifference;

            if (timeStartMinutesNew < minMinutes) {
                timeStartMinutesNew = 0;
                timeEndMinutesNew = slot.duration;
            }

            if (timeEndMinutesNew > maxMinutes) {
                timeStartMinutesNew = maxMinutes - slot.duration;
                timeEndMinutesNew = maxMinutes;
            }

            slot.time_start = minutesToTime(timeStartMinutesNew);
            slot.time_end = minutesToTime(timeEndMinutesNew);
        }

        // Date
        const slotDate = slot.localDate;
        slot.localDate = slotDate.plusDays(daysDifference);
        slot.date = slot.localDate.format(dateformatter);

        if (mode === 'project') {
            slot.task_id = slotTask;
        } else if (mode === 'person') {
            slot.persons = slotPersons;
        }  else if (mode === 'equipment') {
            slot.equipments = slotEquipments;
        } else if (mode === 'team') {
            slot.teams = slotTeams;
            slot.persons = slotPersons;
            slot.equipments = slotEquipments;
        }
        slots[slotId] = slot;
    });
    return [slots, persons, equipments, tasks, teams];
}
